import { defineCopy } from '@/copy';

/**
 * sessions strings. The UI says "run", the code says "session". Voice: short, funny, rib the
 * flake and never the person. No em dashes.
 */
export const copy = defineCopy('sessions', {
  common: {
    loading: 'Warming up',
    failed: 'That did not go through. Try again.',
    back: 'Back',
    save: 'Save',
    cancel: 'Never mind',
    done: 'Done',
    open: 'Open',
    you: 'You',
    guest: 'Guest',
    soon: 'soon',
  },
  tags: {
    live: 'LIVE',
    open: 'Check-in open',
    planned: 'Planned',
    wrapped: 'Wrapped',
    cancelled: 'Called off',
    popup: 'Pop-up',
    weekly: 'every week',
    everyN: 'every {n} weeks',
    late: 'late',
    here: 'here',
    full: 'Full',
    invited: 'Invite only',
    skipped: 'Skipped',
  },
  runs: {
    title: 'Runs',
    line: 'Who is playing, where, and who is lying about "maybe".',
    upcoming: 'Upcoming',
    past: 'Past',
    emptyUpcoming: 'No runs on the calendar. Somebody has to be the adult.',
    emptyPast: 'No history. Every legend starts with a first run.',
    schedule: 'Schedule one',
    newRun: 'New run',
    courts: 'Courts',
    live: 'Live now',
  },
  status: {
    in: 'In',
    maybe: 'Maybe',
    out: 'Out',
    waitlist: 'Waitlist',
    none: 'No answer',
    inLine: "You're in",
    maybeLine: 'Maybe',
    outLine: 'Out',
    waitLine: '#{n} in line',
  },
  counts: {
    inOf: '{in}/{cap} in',
    inOnly: '{in} in',
    waiting: '{n} waiting',
    maybe: '{n} maybe',
    here: '{n} here',
    games: '{n} games',
    game: '1 game',
    spotsLeft: '{n} left',
  },
  when: {
    now: 'now',
    tonight: 'tonight',
    tomorrow: 'tomorrow',
    today: 'today',
    inMinutes: 'in {n} min',
    inHours: 'in {n} h',
    inDays: 'in {n} days',
    yesterday: 'yesterday',
    daysAgo: '{n} days ago',
    ago: '{n} h ago',
  },
  rsvp: {
    label: 'Your answer',
    in: "I'm in",
    maybe: 'Maybe',
    out: "Can't",
    inShort: 'In',
    waitlisted: 'You are on the waitlist, #{n} in line.',
    promoted: "You're in. Somebody blinked.",
    addGuest: '+ guest',
    guests: 'Guests',
    guestsLine: '+{n} guest',
    guestsLines: '+{n} guests',
    guestWaits: 'Crew before plus-ones. Your guest waits for a seat.',
    note: 'Running late?',
    notePlaceholder: '10 min late',
    saved: 'Locked in.',
    savedMaybe: 'Noted. We will ask again.',
    savedOut: 'Fine. Your spot goes to someone who shows up.',
    savedWait: 'You are #{n} in line.',
    queued: 'Saved on your phone. It goes out when you have signal.',
    full: 'Run is full. In puts you on the waitlist.',
    closed: 'This run is over.',
    cancelled: 'Called off.',
    forGuest: 'Answer for a guest',
  },
  detail: {
    title: 'Run',
    when: 'When',
    where: 'Where',
    noCourt: 'Court TBD. Ask the group chat.',
    maps: 'Open in Maps',
    who: 'Who is in',
    inTitle: 'In',
    maybeTitle: 'Maybe',
    waitTitle: 'Waitlist',
    outTitle: 'Out',
    nobody: 'Nobody yet. Somebody go first.',
    notes: 'Notes',
    noNotes: 'No notes. Bring a ball.',
    attached: 'Played today',
    attachedEmpty: 'Nothing logged yet.',
    notFound: 'That run does not exist, or it is not for you.',
    imHere: "I'm here",
    youreHere: "You're in. Lace up.",
    goLive: "We're on",
    liveHub: 'Run day hub',
    recap: 'See the recap',
    cancelledBanner: 'Called off.',
    cancelledNote: 'Save your knees for next week.',
    wrappedBanner: 'This run is in the books.',
    overflow: 'More',
    edit: 'Edit',
    skip: 'Skip this one',
    cancel: 'Call it off',
    stop: 'Stop the series',
    share: 'Share invite',
    shareText: '{title}, {when}. In or out?',
    copied: 'Link copied. Paste it in the group chat.',
    calendar: 'Add to calendar',
    calendarFail: 'Could not open that. Copy the calendar link from Me instead.',
    capLine: '{n} spots',
    noLimit: 'No limit',
    weekly: 'Every week',
    everyN: 'Every {n} weeks',
    comments: 'Notes from the crew',
    commentPlaceholder: 'Bring the good ball',
    commentAdd: 'Add',
    commentHelp: 'Three one-liners each. No replies, no notifications.',
    commentDelete: 'Delete',
    commentFail: 'Three notes is plenty. Delete one to say more.',
    seriesNote: 'Part of a weekly run',
  },
  scope: {
    title: 'Which runs?',
    one: 'Just this one',
    following: 'This and all after',
    oneHelp: 'The rest of the series stays as it is.',
    followingHelp: 'Moves every later run in the series.',
  },
  confirm: {
    cancelTitle: 'Call it off?',
    cancelBody: 'Everyone who is in gets pinged.',
    cancelHold: 'Hold to call it off',
    skipTitle: 'Skip this week?',
    skipBody: 'The series carries on next week. Everyone who is in gets pinged.',
    skipHold: 'Hold to skip',
    stopTitle: 'Stop the weekly run?',
    stopBody: 'No more runs get made. Upcoming ones are called off and people get pinged.',
    stopHold: 'Hold to stop it',
    done: 'Done.',
    wrapped: 'Wrapped. Good run.',
    skipped: 'Skipped. See you next week.',
    called: 'Called off. The crew has been told.',
    stopped: 'Series stopped.',
  },
  form: {
    newTitle: 'New run',
    editTitle: 'Edit run',
    newLine: 'Pick a court, a night and a cap. Flakes get the waitlist.',
    editLine: 'Moving the run? Everyone gets told.',
    title: 'Name',
    titleHint: 'What the crew calls it',
    defaultTitle: '{day} Run',
    court: 'Court',
    noCourt: 'No court yet',
    newCourt: '+ New court',
    date: 'Night',
    otherDate: 'Another date',
    time: 'Tip off',
    hour: 'Hour',
    minute: 'Minute',
    duration: 'How long',
    minutes: '{n} min',
    capacity: 'Spots',
    capacityHelp: 'Crew first, plus-ones after. The rest wait in line.',
    noLimit: 'No limit',
    repeat: 'Repeat every week',
    repeatHelp: 'The next 8 weeks show up on the calendar. Skip any night.',
    interval: 'Every how many weeks',
    until: 'Until (optional)',
    untilHelp: 'Leave empty to run forever.',
    visibility: 'Who sees it',
    crew: 'Whole crew',
    invited: 'Invite only',
    invitees: 'Who is invited',
    notes: 'Notes',
    notesPlaceholder: 'Bring the good ball.',
    submit: 'Put it on the calendar',
    submitEdit: 'Save changes',
    saving: 'Saving',
    gap: "That time doesn't exist that night (clocks jump). We'll move it to {time}.",
    gapDone: "That time doesn't exist that night (clocks jump). We moved it to {time}.",
    overlap: 'That hour happens twice that night (clocks fall back). We take the first one.',
    overlapDone: 'That hour happens twice that night (clocks fall back). We took the first one.',
    today: 'Today',
    tomorrow: 'Tomorrow',
    created: 'On the calendar. Everyone has been pinged.',
    updated: 'Saved. The crew has been told if it moved.',
    past: 'That night already happened. Pick a later one.',
    needsTitle: 'Give it a name.',
    startNow: "We're hooping now",
    startNowHelp: 'Starts a live run right now and checks you in.',
    popupTitle: 'Pop-up run',
    popupFail: 'Could not start the pop-up. Try again.',
  },
  courts: {
    title: 'Courts',
    line: 'Where you play. Paste a map link and you are done.',
    empty: 'No courts saved. Where do you even play?',
    add: 'Add a court',
    addTitle: 'New court',
    name: 'Name',
    nameHint: 'Peristeri open court',
    link: 'Map link',
    linkHint: 'Paste a Google or Apple Maps link',
    linkHelp: 'We pick the pin out of it.',
    address: 'Address',
    hoops: 'Hoops',
    lights: 'Has lights',
    notes: 'Notes',
    notesHint: 'The left rim is mean.',
    save: 'Save court',
    archive: 'Remove',
    archiveTitle: 'Remove this court?',
    archiveBody: 'Old runs keep it. You just cannot pick it for new ones.',
    archiveHold: 'Hold to remove',
    saved: 'Court saved.',
    removed: 'Court removed.',
    lights_on: 'Lights',
    hoopsOne: '1 hoop',
    hoopsMany: '{n} hoops',
    edit: 'Edit',
  },
  live: {
    title: 'Run day',
    line: 'Check in at the court. Late is still late.',
    imHere: "I'm here",
    youreHere: "You're in. Lace up.",
    leave: "I'm heading out",
    left: 'Left',
    present: 'Here now',
    presentEmpty: 'Nobody yet. The first one gets to pick the end the sun is on.',
    notHere: 'Not here yet',
    notHereEmpty: 'Everybody who said yes is here.',
    theyreHere: "They're here",
    notHereHelp: 'Checks them in. Good for the friend with no phone.',
    late: 'Fashionably late',
    suggestions: 'What to play',
    start: 'Start something',
    start1v1: '1v1',
    start2v2: '2v2',
    start3v3: '3v3',
    nextGame: 'Next game',
    tally: 'Today so far',
    tallyEmpty: 'No games yet. The scoreboard is lonely.',
    wl: '{w}-{l}',
    attached: 'Logged today',
    wrap: 'Hold to wrap it',
    wrapHelp: 'Ends the run and writes the recap.',
    wrapDone: 'Wrapped. The recap is cooking.',
    goLive: "We're on",
    goLiveHelp: 'Not enough check-ins? Start it anyway.',
    plus: '+{n}',
    undo: 'Undo',
    checkedIn: 'Checked in.',
    checkInFail: 'Could not check in. Try again.',
    queued: 'Saved on your phone. It goes out when you have signal.',
    notLive: 'This run is not live.',
    openRun: 'Open the run page',
    keepGoing: 'Back to the run',
    reopen: 'Reopen the run',
    yourGames: 'Games with this run',
  },
  recap: {
    title: 'Recap',
    line: 'The recap writes itself after the run.',
    mvp: 'MVP of the day',
    games: 'Games',
    minutes: 'Minutes',
    mover: 'Biggest mover',
    hotHand: 'Hot hand',
    hotHandLine: '{made}/{att}',
    present: 'Showed up',
    late: 'Late',
    ghosted: 'Ghosted',
    attendance: 'Who showed',
    noShows: 'Said yes, ghosted',
    empty: 'No games logged. Did you even go?',
    notYet: 'Not wrapped yet. Check back after the run.',
    share: 'Share the recap',
    sharing: 'Building the picture',
    shareFail: 'Could not build the picture. Take a screenshot like it is 2012.',
    nextRun: 'Next run',
    noNext: 'No next run yet',
    scheduleNext: 'Schedule the next one',
    tally: 'The table',
    wl: '{w}W {l}L',
    pts: '{n} pts',
    shareFile: 'tts-run-{date}.png',
    footer: 'TTS Basketball',
    delta: '{n} OVR',
    lab: 'Lab games',
    cardSteady: 'Steady crew',
    cardNobody: 'Nobody qualified',
    cardShowed: '{n} showed up',
    cardLate: '{n} late',
    cardGhosted: '{n} ghosted',
    cardMinutes: '{n} min',
    cardShort: 'Short and sweet',
    cardOvr: '{name} · OVR',
    madeOf: '{made}/{att}',
  },
  nextRun: {
    title: 'Next run',
    empty: 'No runs on the calendar. Somebody has to be the adult.',
    schedule: 'Schedule one',
    liveAt: 'LIVE at {court}',
    liveNoCourt: 'LIVE now',
    liveHere: '{n} here · {g}',
    imHere: "I'm here",
    youreHere: "You're in. Lace up.",
    openHub: 'Run day hub',
    seeAll: 'All runs',
    inLine: 'Your spot is saved',
    plusGuest: '+ guest',
    openRun: 'Details',
    noCourt: 'Court TBD',
    guestsLabel: 'Guests',
    guestsClose: 'Done',
    waitlisted: 'Waitlist, #{n} in line',
  },
  strip: {
    title: 'Runs',
    nextUp: 'Next up',
    live: 'Live now',
    none: 'Nothing on the calendar yet.',
    all: 'All runs',
    hoopingNow: "We're hooping now",
    hoopingHelp: 'Pop-up run. Starts right now, you are checked in.',
    schedule: 'Schedule one',
    started: 'Pop-up run is live. Go get them.',
  },
  attendance: {
    title: 'Attendance',
    line: 'Shows up, every time. Or not. We will know.',
    reliability: 'Reliability',
    streak: 'Streak',
    best: 'Best streak',
    attended: 'Runs',
    last: 'Last 10',
    empty: 'No runs yet. The score starts at 80 for a reason.',
    hidden: 'The crew keeps attendance private.',
    p: 'Here',
    a: 'Missed',
    n: 'Not in',
    badges: {
      never_misses: 'Never Misses',
      early_bird: 'Early Bird',
      day_one: 'Day One',
    },
    badgeHelp: {
      never_misses: '8 runs in a row.',
      early_bird: 'Checked in before tip off 5 times.',
      day_one: 'Was there at the very first run.',
    },
  },
  calendar: {
    title: 'Calendar',
    line: 'Every run on your calendar. It updates when it feels like it. The app knows first.',
    subscribe: 'Subscribe in Apple Calendar',
    google: 'Add to Google',
    copy: 'Copy link',
    copied: 'Link copied.',
    reset: 'Reset link',
    resetTitle: 'Reset your calendar link?',
    resetBody: 'The old link stops working. You will have to subscribe again.',
    resetHold: 'Hold to reset',
    resetDone: 'New link. The old one is dead.',
    refresh: 'Calendars update when they feel like it. The app knows first.',
    loading: 'Making your link',
    failed: 'Could not make your link. Try again.',
    pingsOff: 'Pings are off? Your calendar can nag you instead.',
  },
  ideas: {
    title: 'What to play',
    go: 'Do it',
  },
  errors: {
    notFound: 'That run does not exist.',
    forbidden: 'That one is not yours to change.',
    conflict: 'Too late for that.',
    generic: 'Something broke. Try again.',
  },
});
